import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PlantBadge, plantAlt, PhotoCredit, Section, Spinner } from "../components/Bits";
import { ArrowLeft, Heart, AlertTriangle, Sparkles, FileDown, Share2 } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export default function PlantDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [lookAlikes, setLookAlikes] = useState([]);
  const { user } = useAuth() || {};

  useEffect(() => {
    setLoading(true);
    api.get(`/plants/${slug}`).then((r) => {
      setPlant(r.data);
      if (r.data?.slug && r.data.slug !== slug && r.data.id !== slug) navigate(`/plants/${r.data.slug}`, { replace: true });
    }).finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (!plant?.look_alike_slugs?.length) { setLookAlikes([]); return; }
    api.get("/plants").then((r) => {
      const all = r.data || [];
      setLookAlikes(plant.look_alike_slugs.map((s) => all.find((p) => p.slug === s)).filter(Boolean));
    });
  }, [plant]);

  useEffect(() => {
    if (!user || !plant) return;
    api.get("/favorites").then((r) => {
      setFav((r.data || []).some((p) => p.id === plant.id));
    });
  }, [user, plant]);

  const toggleFav = async () => {
    if (!user) {
      toast.error("Bitte melde dich an, um Favoriten zu speichern.");
      return;
    }
    if (fav) {
      await api.delete(`/favorites/${plant.id}`);
      setFav(false);
      toast.success("Von Merkliste entfernt");
    } else {
      await api.post(`/favorites/${plant.id}`);
      setFav(true);
      toast.success("Zur Merkliste hinzugefügt");
    }
  };

  const generateRecipe = async () => {
    if (!plant) return;
    setGenLoading(true);
    try {
      const r = await api.post("/recipes/generate", { plant_names: [plant.name_de], style: "einfach" });
      setRecipe(r.data.recipe);
    } catch (e) {
      const msg = e?.response?.status === 401
        ? "Bitte kurz einloggen, um KI-Rezepte zu generieren."
        : ("Rezeptgenerierung fehlgeschlagen: " + (e?.response?.data?.detail || e.message || ""));
      toast.error(msg);
    } finally {
      setGenLoading(false);
    }
  };

  if (loading) return <Section><Spinner /></Section>;
  if (!plant) return <Section><div>Nicht gefunden</div></Section>;

  return (
    <div data-testid="plant-detail">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] w-full overflow-hidden">
        <img src={plant.image_url} alt={plantAlt(plant)} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(26,47,28,0.85), rgba(26,47,28,0.1))" }} />
        <PhotoCredit credit={plant.image_credit} className="absolute bottom-2 right-3 z-20 text-[rgba(245,240,232,0.75)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 lg:px-12 h-full flex items-end pb-12">
          <div>
            <Link to="/plants" className="inline-flex items-center gap-2 text-sm mb-4" style={{ color: "rgba(245,240,232,0.85)" }} data-testid="back-to-plants">
              <ArrowLeft className="w-4 h-4" /> Zurück zur Datenbank
            </Link>
            <div className="flex items-center gap-3 mb-3">
              <PlantBadge category={plant.category} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--wp-cream)] tracking-tight">{plant.name_de}</h1>
            <div className="italic text-base md:text-lg mt-2" style={{ color: "rgba(245,240,232,0.85)" }}>
              {plant.name_lat}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/plants/${plant.slug}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors"
                style={{ backgroundColor: "rgba(245,240,232,0.95)", color: "var(--wp-olive)" }}
                data-testid="plant-pdf-btn"
              >
                <FileDown className="w-4 h-4" /> Steckbrief als PDF
              </a>
              <SharePlantButton plant={plant} />
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-display text-2xl mb-3">Über die Pflanze</h2>
              <p className="leading-relaxed" style={{ color: "var(--wp-text-muted)" }}>{plant.description}</p>
            </div>

            {plant.warnings && (
              <div className="rounded-2xl p-6 flex gap-4 items-start" style={{ backgroundColor: "rgba(167,58,58,0.08)", border: "1px solid rgba(167,58,58,0.25)" }}>
                <AlertTriangle className="w-6 h-6 shrink-0" style={{ color: "var(--wp-toxic)" }} />
                <div>
                  <div className="overline mb-2" style={{ color: "var(--wp-toxic)" }}>Wichtiger Hinweis</div>
                  <p className="text-sm leading-relaxed">{plant.warnings}</p>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-6">
              {plant.ingredients && (
                <div className="plant-card p-6">
                  <div className="overline mb-2">Inhaltsstoffe</div>
                  <p className="text-sm leading-relaxed">{plant.ingredients}</p>
                </div>
              )}
              {plant.habitat && (
                <div className="plant-card p-6">
                  <div className="overline mb-2">Standort</div>
                  <p className="text-sm leading-relaxed">{plant.habitat}</p>
                </div>
              )}
              {plant.uses && (
                <div className="plant-card p-6">
                  <div className="overline mb-2">Verwendung</div>
                  <p className="text-sm leading-relaxed">{plant.uses}</p>
                </div>
              )}
              {(plant.look_alikes || lookAlikes.length > 0) && (
                <div className="plant-card p-6">
                  <div className="overline mb-2">Verwechslungen</div>
                  {plant.look_alikes && <p className="text-sm leading-relaxed mb-4">{plant.look_alikes}</p>}
                  {lookAlikes.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="lookalike-grid">
                      {lookAlikes.map((p) => (
                        <Link key={p.slug} to={`/plants/${p.slug}`} className="block group rounded-xl overflow-hidden border hover:shadow-md transition-shadow" style={{ borderColor: "var(--wp-border)" }} data-testid={`lookalike-${p.slug}`}>
                          <img src={p.image_url} alt={plantAlt(p)} loading="lazy" className="w-full aspect-square object-cover group-hover:scale-105 transition-transform" />
                          <div className="p-2">
                            <div className="text-xs font-medium truncate">{p.name_de}</div>
                            <div className="text-[10px] italic opacity-70 truncate">{p.name_lat}</div>
                            <div className="mt-1"><PlantBadge category={p.category} /></div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {plant.historical_background && (
                <div className="plant-card p-6" data-testid="plant-historical-background" style={{ borderLeft: "3px solid var(--wp-olive)" }}>
                  <div className="overline mb-3 flex items-center gap-2">
                    <i className="fa-solid fa-scroll" style={{ color: "var(--wp-olive)" }} />
                    Historischer Hintergrund
                  </div>
                  <p className="font-display italic text-base md:text-lg leading-relaxed whitespace-pre-line" style={{ color: "var(--wp-forest)" }}>
                    {plant.historical_background}
                  </p>
                </div>
              )}
            </div>

            {plant.images && plant.images.length > 0 && (
              <div>
                <div className="overline mb-3">Galerie</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {plant.images.map((img, i) => {
                    const credit = (plant.image_credits || []).find((c) => img === c.url || img.endsWith(c.url));
                    const partKey = credit?.slot || (plant.image_labels || []).find((l) => l.url === img)?.slot || Object.entries(plant.photo_parts || {}).find(([, u]) => img === u || img.endsWith(u))?.[0];
                    const partLabel = { blatt: "Blatt", bluete: "Blüte", frucht: "Frucht", standort: "Standort", detail: "Detail" }[partKey];
                    return (
                      <div key={i} className="relative">
                        <a href={img} target="_blank" rel="noopener noreferrer" className="block group" data-testid={`plant-gallery-${i}`}>
                          <img src={img} alt={`${plant.name_de}${partLabel ? ` – ${partLabel}` : ` ${i + 1}`}`} className="w-full aspect-square object-cover rounded-xl group-hover:opacity-90 transition-opacity" />
                        </a>
                        {partLabel && <div className="mt-1 text-xs font-medium" style={{ color: "var(--wp-olive)" }} data-testid={`plant-gallery-label-${i}`}>{partLabel}</div>}
                        {credit && <PhotoCredit credit={credit} className="block mt-1 text-[10px] opacity-70" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {plant.recipes && plant.recipes.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-4">Rezepte</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {plant.recipes.map((r, i) => (
                    <div key={i} className="plant-card p-5" data-testid={`recipe-${i}`}>
                      <div className="font-display text-lg mb-1">{r.title}</div>
                      <p className="text-sm opacity-80">{r.summary}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="plant-card p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="overline mb-1">KI-Rezept</div>
                  <h3 className="font-display text-xl">Eigenes Rezept generieren</h3>
                  <p className="text-sm opacity-70 mt-1">Lass dir mit Claude Sonnet 4.5 ein Rezept zu {plant.name_de} erstellen.</p>
                </div>
                <button onClick={generateRecipe} disabled={genLoading || plant.category === "toxic"} className="btn-primary" data-testid="generate-recipe-btn">
                  <Sparkles className="w-4 h-4" /> {genLoading ? "Generiere…" : "Rezept generieren"}
                </button>
              </div>
              {plant.category === "toxic" && (
                <div className="text-xs mt-3" style={{ color: "var(--wp-toxic)" }}>Giftige Pflanze – kein Rezept möglich.</div>
              )}
              {recipe && (
                <pre className="mt-6 whitespace-pre-wrap text-sm leading-relaxed bg-[var(--wp-cream)] rounded-xl p-5 font-body" data-testid="generated-recipe">{recipe}</pre>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="plant-card p-6 sticky top-28">
              <button onClick={toggleFav} className={`w-full ${fav ? "btn-primary" : "btn-secondary"} justify-center`} data-testid="favorite-btn">
                <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} /> {fav ? "Gemerkt" : "Merken"}
              </button>

              <div className="mt-6">
                <div className="overline mb-3">Saison</div>
                <div className="grid grid-cols-6 gap-1.5">
                  {MONTHS.map((m, i) => {
                    const active = (plant.season || []).includes(i + 1);
                    return (
                      <div
                        key={m}
                        className={`text-center text-[10px] py-2 rounded-lg font-medium ${active ? "text-[var(--wp-cream)]" : "opacity-50"}`}
                        style={{ backgroundColor: active ? "var(--wp-primary)" : "var(--wp-cream)", border: "1px solid var(--wp-border)" }}
                      >
                        {m}
                      </div>
                    );
                  })}
                </div>
              </div>

              {plant.tags && plant.tags.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {plant.tags.map((t) => (
                      <span key={t} className="text-xs px-3 py-1 rounded-full bg-[var(--wp-cream)] border" style={{ borderColor: "var(--wp-border)" }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}

function SharePlantButton({ plant }) {
  const [open, setOpen] = useState(false);
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
  const shareUrl = `${base}/api/share/plants/${plant.slug}`;
  const shareTitle = `${plant.name_de} – wilde Pflanzen Luxemburgs`;
  const shareText = plant.short_description || "";

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return true;
      } catch {
        /* user cancelled */
        return true;
      }
    }
    return false;
  };

  const onClick = async () => {
    const native = await tryNativeShare();
    if (!native) setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link kopiert");
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  const enc = encodeURIComponent;
  const links = [
    { label: "WhatsApp", url: `https://wa.me/?text=${enc(shareTitle + " — " + shareUrl)}`, testid: "share-whatsapp" },
    { label: "Telegram", url: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareTitle)}`, testid: "share-telegram" },
    { label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, testid: "share-facebook" },
    { label: "E-Mail", url: `mailto:?subject=${enc(shareTitle)}&body=${enc(shareText + "\n\n" + shareUrl)}`, testid: "share-email" },
  ];

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors"
        style={{ borderColor: "rgba(245,240,232,0.5)", color: "rgba(245,240,232,0.95)", backgroundColor: "transparent" }}
        data-testid="plant-share-btn"
      >
        <Share2 className="w-4 h-4" /> Teilen
      </button>
      {open && (
        <div className="absolute z-30 mt-2 right-0 w-56 rounded-xl border bg-white shadow-xl p-2" style={{ borderColor: "var(--wp-border)" }} data-testid="share-menu">
          {links.map((l) => (
            <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm rounded-lg hover:bg-[var(--wp-cream)]" style={{ color: "var(--wp-forest)" }} data-testid={l.testid}>
              {l.label}
            </a>
          ))}
          <button type="button" onClick={() => { copy(); setOpen(false); }} className="block w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-[var(--wp-cream)]" style={{ color: "var(--wp-forest)" }} data-testid="share-copy">
            Link kopieren
          </button>
        </div>
      )}
    </div>
  );
}
